/**
 * TeamEditor — office-user management (quick 260814-ud3 / ClickUp 86bbed5r5).
 *
 * Lists dispatchers and dispatch managers with deactivate / reactivate, role
 * change, and password reset. Drivers are deliberately NOT listed here — they
 * live in Fleet → Drivers, which owns their compliance fields (shift, home yard,
 * CDL, phone). One person, one place to manage them.
 *
 * Every destructive action confirms first and says what will actually happen.
 * "Deactivate" is not reversible-by-accident: it signs the user out everywhere
 * immediately, so the copy says so rather than leaving the admin to find out.
 */

import { useState } from 'react';
import { apiErrorText, roleDisplayLabel } from '@lodex-pro/web-shared';
import { toast } from 'sonner';
import { AddUserForm } from '../fleet/AddUserForm';
import type { CreatableUserRole } from '../fleet/use-create-user';
import {
  useTeam,
  useSetUserStatus,
  useSetUserRole,
  useResetUserPassword,
  isNotInvited,
  type TeamMember,
} from './hooks';
import { EmailStatusBadge, InviteButton, NotInvitedBadge, emailFlaggedText } from './InviteButton';

// DEC-313 — one source of role labels. This file used to carry its own
// five-entry map and fall back to the RAW ROLE CODE for anything missing, so a
// member holding a combined role would have read "DISPATCH_MANAGER_BILLING_MANAGER"
// on screen. roleDisplayLabel is keyed by RoleCode (typed Record), so a new role
// cannot be added without a label.
const roleLabel = (role: string): string => roleDisplayLabel(role) ?? role;

/** This surface creates office users only; drivers are created in Fleet. */
/**
 * Roles this surface can grant, in the order the picker shows them: plain
 * dispatch, plain billing, then the combined ones (DEC-313).
 *
 * The server re-validates against its own ASSIGNABLE_ROLES / CREATABLE_ROLES
 * allowlists, so this list narrows the UI and never the security boundary.
 */
const OFFICE_ROLES: readonly CreatableUserRole[] = [
  'DISPATCHER',
  'DISPATCH_MANAGER',
  'BILLING_STAFF',
  'BILLING_MANAGER',
  'DISPATCHER_BILLING_STAFF',
  'DISPATCHER_BILLING_MANAGER',
  'DISPATCH_MANAGER_BILLING_STAFF',
  'DISPATCH_MANAGER_BILLING_MANAGER',
];

export function MemberRow({ member }: { member: TeamMember }): React.ReactElement {
  const [busy, setBusy] = useState<string | null>(null);
  const active = member.status === 'ACTIVE';
  // Phase 20.6e — an imported person has no login yet. Reset password acts on
  // the Cognito account, which does not exist, so it is hidden rather than
  // offered to fail. Deactivate/Reactivate IS offered: with no login there is
  // nothing to sign out, so the server just records the status change.
  const invited = !isNotInvited(member);

  const status = useSetUserStatus(member.id, () => {
    setBusy(null);
    toast.success(
      active
        ? invited
          ? `${member.fullName} deactivated and signed out everywhere`
          : `${member.fullName} deactivated`
        : `${member.fullName} reactivated`,
    );
  });
  const role = useSetUserRole(member.id, () => {
    setBusy(null);
    toast.success(`${member.fullName}'s role updated`);
  });
  const reset = useResetUserPassword(member.id, (result) => {
    setBusy(null);
    // Quick 260821-ses — same shape as the create path above. A reset that
    // rotated the password but could not deliver it is NOT a success story: the
    // member is now locked out until someone resends, and saying "emailed" would
    // send the operator away believing it worked.
    if (result.emailSkipped === 'EMAIL_BOUNCING') {
      // 0227 — nothing was rotated: the password would only have gone to a flagged address.
      toast.error(`${emailFlaggedText(member)} The password was not reset.`);
    } else if (result.emailed) {
      toast.success(`Temporary password emailed to ${member.email}`);
    } else {
      toast.error(
        `${member.fullName}'s password was reset, but the email did not send — they cannot sign in until you resend it.`,
      );
    }
  });

  const fail = (e: unknown): void => {
    setBusy(null);
    toast.error(apiErrorText(e, 'That did not work. Try again.'));
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderBottom: '1px solid var(--color-border-secondary)',
        fontSize: 14,
        opacity: active ? 1 : 0.55,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>
          {member.fullName}
          {member.isSelf && (
            <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}> · you</span>
          )}
          {!active && (
            <span style={{ fontSize: 12, color: 'var(--color-warning)' }}> · deactivated</span>
          )}
          {!invited && (
            <span style={{ marginLeft: 8 }}>
              <NotInvitedBadge />
            </span>
          )}
          {member.emailStatus !== undefined && member.emailStatus !== 'OK' && (
            <span style={{ marginLeft: 8 }}>
              <EmailStatusBadge status={member.emailStatus} />
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
          {member.email} · {roleLabel(member.role)}
        </div>
      </div>

      {/* Self-actions are hidden, not merely rejected: the server blocks them, but
          offering a button that always fails is worse than not offering it. */}
      {!member.isSelf && (
        <select
          aria-label={`Role for ${member.fullName}`}
          value={member.role}
          disabled={busy !== null || !active}
          onChange={(e) => {
            setBusy('role');
            role.mutate({ role: e.target.value }, { onError: fail });
          }}
          style={{ minHeight: 34, fontSize: 13 }}
        >
          {OFFICE_ROLES.map((r) => (
            <option key={r} value={r}>
              {roleLabel(r)}
            </option>
          ))}
        </select>
      )}

      {active && !invited && <InviteButton person={member} />}

      {active && invited && (
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => {
            setBusy('reset');
            reset.mutate({}, { onError: fail });
          }}
          style={{ minHeight: 34, fontSize: 13 }}
        >
          {busy === 'reset' ? 'Sending…' : 'Reset password'}
        </button>
      )}

      {!member.isSelf && (
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => {
            const ok = window.confirm(
              active
                ? invited
                  ? `Deactivate ${member.fullName}?\n\nThey will be signed out everywhere immediately and will not be able to sign in again until reactivated.`
                  : `Deactivate ${member.fullName}?\n\nThey have not been invited, so they have no login. They cannot be invited while deactivated.`
                : invited
                  ? `Reactivate ${member.fullName}?\n\nThey will be able to sign in again.`
                  : `Reactivate ${member.fullName}?\n\nThey still have no login until you invite them.`,
            );
            if (!ok) return;
            setBusy('status');
            status.mutate({ active: !active }, { onError: fail });
          }}
          style={{ minHeight: 34, fontSize: 13 }}
        >
          {busy === 'status' ? '…' : active ? 'Deactivate' : 'Reactivate'}
        </button>
      )}
    </div>
  );
}

export function TeamEditor(): React.ReactElement {
  const { data, isLoading, isError } = useTeam();
  const [adding, setAdding] = useState(false);
  const members = data?.users ?? [];

  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <h2 style={{ fontSize: 15, fontWeight: 600 }}>Office users ({members.length})</h2>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} style={{ minHeight: 34 }}>
            + New team member
          </button>
        )}
      </div>

      {adding && (
        <div
          style={{
            border: '1px solid var(--color-border-primary)',
            borderRadius: 8,
            padding: 14,
            marginBottom: 12,
          }}
        >
          <AddUserForm
            allowedRoles={OFFICE_ROLES}
            onCreated={(result) => {
              setAdding(false);
              toast.success(
                result.inviteEmailed === false
                  ? `${result.fullName} was created, but the invite email did not send — use Reset password to try again.`
                  : `${result.fullName} added — a temporary password was emailed to ${result.email}`,
              );
            }}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}

      {isLoading && <p style={{ fontSize: 12 }}>Loading…</p>}
      {isError && (
        <p style={{ fontSize: 13, color: 'var(--color-danger)' }}>Could not load the team.</p>
      )}
      {members.map((m) => (
        <MemberRow key={m.id} member={m} />
      ))}
      {!isLoading && members.length === 0 && (
        <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
          No office users yet. Drivers are managed in Fleet → Drivers.
        </p>
      )}
    </section>
  );
}
